/** Pure game logic (unit-tested). Content always comes from the API's lesson JSON. */

export interface WordItem {
  word: string;
  gloss: string;
  picture?: string;
}
export interface ToneSet {
  base: string;
  options: WordItem[];
}
export interface NumberItem {
  value: number;
  word: string;
}

export interface Round<T> {
  answer: T;
  choices: T[];
}

/** Deterministic-when-seeded shuffle (Fisher–Yates) so tests are stable. */
export function shuffle<T>(items: readonly T[], rand: () => number = Math.random): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

export function seeded(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 2 ** 32;
  };
}

/** Picture → word: each item becomes a round with up to `choiceCount` options including the answer. */
export function pictureMatchRounds(items: WordItem[], choiceCount = 3, rand: () => number = Math.random): Round<WordItem>[] {
  return shuffle(items, rand).map((answer) => {
    const distractors = shuffle(
      items.filter((i) => i.word !== answer.word),
      rand,
    ).slice(0, Math.max(0, choiceCount - 1));
    return { answer, choices: shuffle([answer, ...distractors], rand) };
  });
}

/** Tone marks: same letters, different tones. Every option in the set is a round; all options are choices. */
export function toneRounds(sets: ToneSet[], rand: () => number = Math.random): Round<WordItem>[] {
  return shuffle(
    sets.flatMap((s) => s.options.map((answer) => ({ answer, choices: shuffle(s.options, rand) }))),
    rand,
  );
}

/** Counting: show N cowries, pick the Yoruba number word. */
export function countingRounds(numbers: NumberItem[], choiceCount = 4, rand: () => number = Math.random): Round<NumberItem>[] {
  const ordered = [...numbers].sort((a, b) => a.value - b.value);
  return shuffle(ordered, rand).map((answer) => {
    const near = ordered.filter((n) => n.value !== answer.value).sort((a, b) => Math.abs(a.value - answer.value) - Math.abs(b.value - answer.value));
    return { answer, choices: shuffle([answer, ...near.slice(0, choiceCount - 1)], rand) };
  });
}

export function scorePercent(correctFirstTry: number, total: number): number {
  return total === 0 ? 0 : Math.round((correctFirstTry / total) * 100);
}

/** Strip tone marks + under-dots: shows children that "oko" hides several different words. */
export function stripDiacritics(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').normalize('NFC');
}
